import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, Paperclip, X } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const initialForm = { name: "", email: "", phone: "", service: "", area: "", timing: "", message: "" };

const fieldCls =
  "bg-elevated border-white/10 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-gold rounded-none h-12";

export const QuoteForm = () => {
  const [form, setForm] = useState(initialForm);
  const [file, setFile] = useState(null);
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande: massimo 10 MB");
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.service) {
      toast.error("Seleziona il tipo di intervento");
      return;
    }
    if (!consent) {
      toast.error("Devi accettare l'informativa privacy per inviare la richiesta");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => v && fd.append(k, v));
      fd.append("privacy_consent", "true");
      if (file) fd.append("attachment", file);
      await api.post("/quotes", fd);
      toast.success("Richiesta inviata! Ti ricontatteremo entro 24 ore lavorative.");
      setForm(initialForm);
      setFile(null);
      setConsent(false);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.[0]?.msg || "Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="preventivo" className="py-28 lg:py-40 bg-surface border-y border-white/10" data-testid="quote-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="min-w-0"
        >
          <p className="font-oswald text-gold uppercase tracking-[0.35em] text-sm mb-6">Preventivo gratuito</p>
          <h2 className="font-oswald uppercase text-3xl md:text-5xl tracking-[0.08em] leading-tight mb-8" data-testid="quote-title">
            Raccontaci il tuo <span className="text-gold">progetto</span>
          </h2>
          <p className="text-muted-foreground leading-loose max-w-md mb-12">
            Compila il modulo indicando le tue esigenze: più dettagli ci dai, più preciso sarà il nostro
            riscontro. Ti ricontatteremo entro 24 ore lavorative per fissare un sopralluogo gratuito.
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
          className="min-w-0 bg-ink border border-white/10 p-6 sm:p-8 lg:p-12 flex flex-col gap-6"
          data-testid="quote-form"
        >
          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Nome e Cognome *</label>
              <Input required value={form.name} onChange={set("name")} placeholder="Mario Rossi" className={fieldCls} data-testid="quote-input-name" />
            </div>
            <div>
              <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Telefono *</label>
              <Input required type="tel" value={form.phone} onChange={set("phone")} placeholder="+39 ..." className={fieldCls} data-testid="quote-input-phone" />
            </div>
          </div>

          <div>
            <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Email *</label>
            <Input required type="email" value={form.email} onChange={set("email")} placeholder="mario.rossi@email.it" className={fieldCls} data-testid="quote-input-email" />
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Tipo di intervento *</label>
              <Select value={form.service} onValueChange={set("service")}>
                <SelectTrigger className={fieldCls} data-testid="quote-select-service">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent className="bg-elevated border-white/10 text-foreground rounded-none">
                  <SelectItem value="Ristrutturazione completa">Ristrutturazione completa</SelectItem>
                  <SelectItem value="Bagno">Bagno</SelectItem>
                  <SelectItem value="Cucina">Cucina</SelectItem>
                  <SelectItem value="Cappotto / Efficientamento energetico">Cappotto / Efficientamento</SelectItem>
                  <SelectItem value="Manutenzione">Manutenzione</SelectItem>
                  <SelectItem value="Altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Metratura indicativa</label>
              <Select value={form.area} onValueChange={set("area")}>
                <SelectTrigger className={fieldCls} data-testid="quote-select-area">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent className="bg-elevated border-white/10 text-foreground rounded-none">
                  <SelectItem value="Fino a 50 mq">Fino a 50 mq</SelectItem>
                  <SelectItem value="50 - 100 mq">50 - 100 mq</SelectItem>
                  <SelectItem value="100 - 150 mq">100 - 150 mq</SelectItem>
                  <SelectItem value="Oltre 150 mq">Oltre 150 mq</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            <div>
              <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Tempistiche</label>
              <Select value={form.timing} onValueChange={set("timing")}>
                <SelectTrigger className={fieldCls} data-testid="quote-select-timing">
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent className="bg-elevated border-white/10 text-foreground rounded-none">
                  <SelectItem value="Il prima possibile">Il prima possibile</SelectItem>
                  <SelectItem value="Entro 1-3 mesi">Entro 1-3 mesi</SelectItem>
                  <SelectItem value="Entro 3-6 mesi">Entro 3-6 mesi</SelectItem>
                  <SelectItem value="Sto solo valutando">Sto solo valutando</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">Descrivi le tue esigenze</label>
            <Textarea
              value={form.message}
              onChange={set("message")}
              placeholder="Es. vorrei rifare completamente il bagno di 5 mq, spostare la doccia e sostituire i rivestimenti..."
              className="bg-elevated border-white/10 text-foreground placeholder:text-muted-foreground/50 focus-visible:ring-gold rounded-none min-h-32"
              data-testid="quote-input-message"
            />
          </div>

          <div>
            <label className="font-oswald uppercase text-xs tracking-[0.2em] text-muted-foreground block mb-3">
              Allegato — foto o piantina (facoltativo)
            </label>
            <label
              htmlFor="quote-attachment-input"
              data-testid="quote-attachment-label"
              className="flex items-center gap-4 bg-elevated border border-dashed border-white/20 px-5 py-4 cursor-pointer hover:border-gold transition-colors duration-300"
            >
              <Paperclip className="w-4 h-4 text-gold shrink-0" />
              <span className="text-sm text-muted-foreground truncate flex-1 min-w-0">
                {file ? file.name : "Carica foto o piantina — JPG, PNG, WEBP o PDF, max 10 MB"}
              </span>
              {file && (
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => { e.preventDefault(); setFile(null); document.getElementById("quote-attachment-input").value = ""; }}
                  onKeyDown={(e) => e.key === "Enter" && setFile(null)}
                  data-testid="quote-attachment-remove"
                  className="text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                  title="Rimuovi allegato"
                >
                  <X className="w-4 h-4" />
                </span>
              )}
            </label>
            <input
              id="quote-attachment-input"
              data-testid="quote-attachment-input"
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={handleFile}
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="quote-privacy-consent">
            <Checkbox
              checked={consent}
              onCheckedChange={(v) => setConsent(v === true)}
              data-testid="quote-privacy-checkbox"
              className="mt-0.5 border-white/30 rounded-none data-[state=checked]:bg-gold data-[state=checked]:text-ink data-[state=checked]:border-gold"
            />
            <span className="text-xs text-muted-foreground leading-relaxed">
              Ho letto l'
              <Link to="/privacy" target="_blank" data-testid="quote-privacy-link" className="text-gold underline underline-offset-2 hover:text-gold-hover transition-colors">
                informativa privacy
              </Link>{" "}
              e acconsento al trattamento dei miei dati per essere ricontattato in merito alla mia richiesta. *
            </span>
          </label>

          <button
            type="submit"
            disabled={loading}
            data-testid="quote-submit-btn"
            className="btn-tracer mt-2 bg-gold text-ink font-oswald uppercase tracking-[0.15em] px-9 py-4 flex items-center justify-center gap-3 hover:bg-gold-hover transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {loading ? "Invio in corso..." : "Invia richiesta"}
          </button>
          <p className="text-xs text-muted-foreground/60 leading-relaxed">
            I tuoi dati non saranno ceduti a terzi e verranno usati solo per rispondere alla tua richiesta.
          </p>
        </motion.form>
      </div>
    </section>
  );
};
